// src/utils/debounce.js
var debounce = (func, timeout = 300) => {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      func.apply(func, args);
    }, timeout);
  };
};
var debounce_default = debounce;

// src/extenders/search-blog.js
var SearchBlog = class extends HTMLElement {
  constructor() {
    super();
    this.form = null;
    this.input = null;
    this.resultsContainer = null;
    this.loadMoreButton = null;
    this.emptyState = null;
    this.defaultResults = null;
    this.currentTerm = "";
    this.currentPage = 1;
    this.isLoading = false;
    this.hasMore = false;
    this.isInitialized = false;
  }
  connectedCallback() {
    var _a;
    if (this.isInitialized) {
      return;
    }
    this.blogPath = this.dataset.blogPath || "/blogs/inspiration";
    this.blogHandle = this.getBlogHandle(this.blogPath);
    this.readMoreLabel = this.dataset.readMoreLabel || "Read more";
    this.imageHeight = this.dataset.imageHeight || "240";
    const parsedPageSize = Number.parseInt(this.dataset.pageSize || "10", 10);
    this.pageSize = Number.isNaN(parsedPageSize) ? 10 : parsedPageSize;
    this.form = this.querySelector("[data-blog-search-form]");
    this.input = this.querySelector("[data-blog-search-input]");
    this.resultsContainer = this.querySelector("[data-blog-search-results]");
    this.loadMoreButton = this.querySelector("[data-blog-search-load-more]");
    this.emptyState = this.querySelector("[data-blog-search-empty]");
    this.defaultResults = (_a = this.parentElement) == null ? void 0 : _a.querySelector("[data-blog-default-results]");
    if (!this.form || !this.input || !this.resultsContainer || !this.emptyState || !this.defaultResults) {
      return;
    }
    this.form.addEventListener("submit", (event) => {
      event.preventDefault();
      this.performSearch(this.input.value);
    });
    this.input.addEventListener("input", debounce_default((event) => {
      this.performSearch(event.target.value);
    }, 250));
    if (this.loadMoreButton) {
      this.loadMoreButton.addEventListener("click", () => {
        this.loadMore();
      });
    }
    this.isInitialized = true;
  }
  /**
   * Fetches and renders matching blog articles for a search term.
   * @param {string} rawTerm
   * @returns {Promise<void>}
   */
  async performSearch(rawTerm) {
    const term = rawTerm.trim();
    if (!term) {
      this.resetSearchState();
      return;
    }
    this.currentTerm = term;
    this.currentPage = 1;
    this.hasMore = false;
    this.showResultsContainer();
    this.resultsContainer.innerHTML = `<p class="text-body-regular-s text-color-off-black">${window.translations.search_for_articles}</p>`;
    this.emptyState.classList.add("hidden");
    this.defaultResults.classList.add("hidden");
    this.hideLoadMoreButton();
    try {
      const { cardsMarkup, hasMore } = await this.fetchSearchPage(term, 1);
      if (!cardsMarkup) {
        this.hideResultsContainer();
        this.emptyState.classList.remove("hidden");
        return;
      }
      this.resultsContainer.innerHTML = cardsMarkup;
      this.showResultsContainer();
      this.hasMore = hasMore;
      this.toggleLoadMoreButton();
    } catch (error) {
      console.error(error);
      this.resetSearchState();
    }
  }
  /**
   * Loads and appends the next search page.
   * @returns {Promise<void>}
   */
  async loadMore() {
    var _a, _b;
    if (!this.currentTerm || this.isLoading || !this.hasMore) {
      return;
    }
    this.isLoading = true;
    (_a = this.loadMoreButton) == null ? void 0 : _a.classList.add("opacity-50", "pointer-events-none");
    try {
      const nextPage = this.currentPage + 1;
      const { cardsMarkup, hasMore } = await this.fetchSearchPage(this.currentTerm, nextPage);
      if (cardsMarkup) {
        this.resultsContainer.insertAdjacentHTML("beforeend", cardsMarkup);
        this.currentPage = nextPage;
      }
      this.hasMore = hasMore && Boolean(cardsMarkup);
      this.toggleLoadMoreButton();
    } catch (error) {
      console.error(error);
      this.hasMore = false;
      this.toggleLoadMoreButton();
    } finally {
      this.isLoading = false;
      (_b = this.loadMoreButton) == null ? void 0 : _b.classList.remove("opacity-50", "pointer-events-none");
    }
  }
  /**
   * Fetches and parses a paginated blog search page from the section rendering API.
   * @param {string} term
   * @param {number} page
   * @returns {Promise<{ cardsMarkup: string, hasMore: boolean }>}
   */
  async fetchSearchPage(term, page) {
    const url = new URL(`${window.Shopify.routes.root}search`, window.location.origin);
    url.searchParams.set("q", term);
    url.searchParams.set("type", "article");
    url.searchParams.set("page", String(page));
    url.searchParams.set("section_id", "blog-search-results");
    url.searchParams.set("limit", String(this.pageSize));
    url.searchParams.set("blog_path", this.blogPath);
    url.searchParams.set("blog_handle", this.blogHandle);
    url.searchParams.set("image_height", this.imageHeight);
    const response = await fetch(url.toString());
    if (!response.ok) {
      throw new Error("Failed to fetch blog search results.");
    }
    const html = await response.text();
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    const cardsContainer = doc.querySelector("[data-blog-search-cards]");
    const hasMoreElement = doc.querySelector("[data-blog-search-has-more]");
    const cardsMarkup = this.filterCardsByBlogPath((cardsContainer == null ? void 0 : cardsContainer.innerHTML.trim()) || "");
    const hasMore = (hasMoreElement == null ? void 0 : hasMoreElement.dataset.blogSearchHasMore) === "true";
    return { cardsMarkup, hasMore };
  }
  /**
   * Returns the blog handle from a blog path like "/blogs/inspiratie".
   * @param {string} path
   * @returns {string}
   */
  getBlogHandle(path) {
    const rawPath = String(path || "");
    const match = rawPath.match(/\/blogs\/([^/?#]+)/);
    return (match == null ? void 0 : match[1]) || "";
  }
  /**
   * Client-side safety filter so only the configured blog articles are rendered.
   * @param {string} cardsMarkup
   * @returns {string}
   */
  filterCardsByBlogPath(cardsMarkup) {
    if (!cardsMarkup) {
      return "";
    }
    const normalizedBlogPath = this.blogPath.endsWith("/") ? this.blogPath : `${this.blogPath}/`;
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div>${cardsMarkup}</div>`, "text/html");
    const wrapper = doc.body.firstElementChild;
    if (!wrapper) {
      return "";
    }
    Array.from(wrapper.children).forEach((card) => {
      const articleLink = card.querySelector("a[href]");
      if (!articleLink) {
        card.remove();
        return;
      }
      const articleUrl = new URL(articleLink.getAttribute("href"), window.location.origin);
      if (!articleUrl.pathname.includes(normalizedBlogPath)) {
        card.remove();
      }
    });
    return wrapper.innerHTML.trim();
  }
  /**
   * Builds the Shopify predictive search URL for articles.
   * @param {string} term
   * @returns {URL}
   */
  buildUrl(term) {
    const url = new URL(`${window.Shopify.routes.root}search/suggest.json`, window.location.origin);
    url.searchParams.set("q", term);
    url.searchParams.set("resources[type]", "article");
    url.searchParams.set("resources[options][fields]", "title,body,tag,author");
    url.searchParams.set("resources[limit]", "10");
    url.searchParams.set("resources[limit_scope]", "each");
    return url;
  }
  /**
   * Filters predictive search results to the configured blog path.
   * @param {any} responseData
   * @returns {Array<any>}
   */
  extractBlogArticles(responseData) {
    var _a, _b;
    const articles = ((_b = (_a = responseData == null ? void 0 : responseData.resources) == null ? void 0 : _a.results) == null ? void 0 : _b.articles) || [];
    const normalizedBlogPath = this.blogPath.endsWith("/") ? this.blogPath : `${this.blogPath}/`;
    return articles.filter((article) => {
      if (!(article == null ? void 0 : article.url)) {
        return false;
      }
      return article.url.includes(normalizedBlogPath);
    });
  }
  /**
   * Renders article cards into the results container.
   * @param {Array<any>} articles
   * @returns {void}
   */
  renderResults(articles) {
    const resultsMarkup = articles.map((article) => {
      var _a, _b, _c, _d, _e, _f, _g;
      const title = this.escapeHtml(article.title || "");
      const articleUrl = article.url;
      const rawExcerpt = article.summary || article.body || article.excerpt || article.content || "";
      const excerpt = this.escapeHtml(this.truncateWords(this.stripHtml(rawExcerpt), 16));
      const imageUrl = ((_a = article == null ? void 0 : article.image) == null ? void 0 : _a.url) || ((_b = article == null ? void 0 : article.featured_image) == null ? void 0 : _b.url) || (article == null ? void 0 : article.featured_image);
      const rawAuthor = ((_e = (_d = (_c = article == null ? void 0 : article.metafields) == null ? void 0 : _c.custom) == null ? void 0 : _d.custom_author) == null ? void 0 : _e.value) || ((_g = (_f = article == null ? void 0 : article.metafields) == null ? void 0 : _f.custom) == null ? void 0 : _g.custom_author) || "";
      const author = this.escapeHtml(String(rawAuthor).trim());
      return `
        <div class="flex overflow-hidden flex-col w-full h-full rounded-md bg-main-soft-white">
          <a href="${articleUrl}" class="overflow-hidden" style="max-height: ${this.imageHeight}px;">
            <img src="${imageUrl}" alt="${title}" class="aspect-[442/240] object-cover md:h-full w-full">
          </a>
          <div class="flex flex-col h-fit p-2x lg:p-3x gap-2x">
            <a href="${articleUrl}">
              <h4 class="text-heading-l lg:text-heading-xl text-color-dark-red line-clamp-2">${title}</h4>
              ${author && `<h2 class="text-heading-xs text-color-dark-red">${author}</h2>`}
            </a>
            ${excerpt && `<div class="text-body-regular-xs lg:text-body-regular-s text-color-off-black">${excerpt}</div>`}
            <a href="${articleUrl}" class="underline text-body-light-xs lg:text-light-regular-s text-color-red underline-offset-2">
              ${this.escapeHtml(this.readMoreLabel)}
            </a>
          </div>
        </div>
      `;
    }).join("");
    this.resultsContainer.innerHTML = resultsMarkup;
    this.showResultsContainer();
  }
  /**
   * Resets search UI to the default state.
   * @returns {void}
   */
  resetSearchState() {
    this.resultsContainer.innerHTML = "";
    this.hideResultsContainer();
    this.emptyState.classList.add("hidden");
    this.defaultResults.classList.remove("hidden");
    this.currentTerm = "";
    this.currentPage = 1;
    this.hasMore = false;
    this.isLoading = false;
    this.hideLoadMoreButton();
  }
  /**
   * Shows and applies layout classes for the results container.
   * @returns {void}
   */
  showResultsContainer() {
    this.resultsContainer.classList.remove("hidden");
    this.resultsContainer.classList.add("flex");
    this.resultsContainer.classList.add("lg:grid");
  }
  /**
   * Hides the results container and removes layout classes.
   * @returns {void}
   */
  hideResultsContainer() {
    this.resultsContainer.classList.add("hidden");
    this.resultsContainer.classList.remove("flex");
    this.resultsContainer.classList.remove("lg:grid");
  }
  /**
   * Shows/hides the load more button based on pagination state.
   * @returns {void}
   */
  toggleLoadMoreButton() {
    if (!this.loadMoreButton) {
      return;
    }
    if (this.hasMore && this.resultsContainer.innerHTML.trim() !== "") {
      this.loadMoreButton.classList.remove("hidden");
      return;
    }
    this.hideLoadMoreButton();
  }
  /**
   * Hides the load more button.
   * @returns {void}
   */
  hideLoadMoreButton() {
    var _a;
    (_a = this.loadMoreButton) == null ? void 0 : _a.classList.add("hidden");
  }
  /**
   * Strips HTML tags and normalizes whitespace.
   * @param {string} value
   * @returns {string}
   */
  stripHtml(value) {
    const normalizedValue = String(value || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
    const parser = new DOMParser();
    const decoded = parser.parseFromString(normalizedValue, "text/html").documentElement.textContent;
    return (decoded || "").replace(/\s+/g, " ").trim();
  }
  /**
   * Truncates a string to a fixed number of words.
   * @param {string} value
   * @param {number} wordCount
   * @returns {string}
   */
  truncateWords(value, wordCount) {
    if (!value) {
      return "";
    }
    const words = value.split(/\s+/);
    if (words.length <= wordCount) {
      return value;
    }
    return `${words.slice(0, wordCount).join(" ")}...`;
  }
  /**
   * Escapes special characters for safe HTML output.
   * @param {string} value
   * @returns {string}
   */
  escapeHtml(value) {
    return value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#039;");
  }
};
if (!customElements.get("search-blog")) {
  customElements.define("search-blog", SearchBlog);
}
